import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { VideoPlayUrlResponse } from '../../../contexts'
import { useApplicationContext } from '../../../contexts'
import type { BasePlayerProps } from '../BasePlayer'
import { BasePlayer } from '../BasePlayer'

export type ByecodePlayerProps = BasePlayerProps

const RENEW_BEFORE_EXPIRE_MS = 30 * 1000
const HLS_URL_REG = /\.m3u8($|[?#])/i

export const ByecodePlayer: React.FC<ByecodePlayerProps> = ({ source: originalUrl, isOrigin, ...rest }) => {
    const { fetchAVPlayUrl } = useApplicationContext()

    const [resolvedSource, setResolvedSource] = useState<string | undefined>(undefined)
    const [resolvedType, setResolvedType] = useState<'mp4' | 'hls' | undefined>(undefined)

    const ctxRef = useRef({ originalUrl, fetchAVPlayUrl })
    ctxRef.current = { originalUrl, fetchAVPlayUrl }

    const renewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const cancelledRef = useRef(false)
    const requestIdRef = useRef(0)

    const clearRenewTimer = () => {
        if (renewTimerRef.current !== null) {
            clearTimeout(renewTimerRef.current)
            renewTimerRef.current = null
        }
    }

    const doFetch = useCallback(async () => {
        const { originalUrl, fetchAVPlayUrl } = ctxRef.current
        if (!originalUrl || !fetchAVPlayUrl) {
            return
        }

        const requestId = ++requestIdRef.current
        const data: VideoPlayUrlResponse | null = await fetchAVPlayUrl(originalUrl, isOrigin)
        if (cancelledRef.current || requestId !== requestIdRef.current || !data?.playUrl) {
            return
        }

        setResolvedSource(data.playUrl)
        setResolvedType(HLS_URL_REG.test(data.playUrl) ? 'hls' : 'mp4')
    }, [isOrigin])

    useEffect(() => {
        cancelledRef.current = false
        requestIdRef.current += 1
        clearRenewTimer()
        setResolvedSource(undefined)
        setResolvedType(undefined)

        doFetch()

        return () => {
            cancelledRef.current = true
            clearRenewTimer()
        }
    }, [originalUrl, fetchAVPlayUrl, doFetch])

    return <BasePlayer {...rest} source={resolvedSource} sourceType={resolvedType} progressKey={originalUrl} />
}
